import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CallSwiperLogo } from "@/components/CallSwiperLogo";
import { ArrowLeft, ArrowRight, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Check your inbox for the reset link.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col justify-between px-5 pt-10 pb-8 sm:p-8 lg:p-14 safe-top-margin safe-bottom">
        <CallSwiperLogo size={26} />
        <div className="max-w-md w-full mx-auto lg:mx-0 animate-fade-in-up">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="back-to-login-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Password reset</p>
          <h1 className="text-3xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Forgot your<br />
            <span className="text-primary">password?</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            No worries — enter the email tied to your Call Swiper account and we'll send you a secure link to set a new one.
          </p>

          {sent ? (
            <div
              data-testid="forgot-sent-panel"
              className="mt-8 rounded-lg border border-primary/25 bg-primary/[0.04] p-4 flex items-start gap-3"
            >
              <MailCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Check your inbox</p>
                <p className="text-muted-foreground mt-1">
                  If an account exists for <span className="font-mono">{email}</span>, we've emailed a reset link. It expires in <strong>1 hour</strong>.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="text-primary hover:underline mt-3 text-xs"
                  data-testid="forgot-resend-btn"
                >
                  Didn't get it? Try a different email.
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="forgot-email-input" className="h-11" />
              </div>
              <Button type="submit" className="w-full h-11 group" disabled={busy} data-testid="forgot-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Send reset link <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" /></>)}
              </Button>
            </form>
          )}
        </div>
        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Call Swiper</p>
      </div>

      {/* Right dark panel (reuse same visual language as Login) */}
      <div className="hidden lg:flex relative overflow-hidden" style={{ backgroundColor: "#0B0F17" }}>
        <div className="relative z-10 px-14 py-16 flex flex-col justify-center w-full max-w-2xl text-white">
          <h2 className="text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05]">
            Secure by design.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/60 max-w-lg">
            Reset links are one-time use, expire in 1 hour, and are cryptographically signed. Your password is only ever stored as a salted hash.
          </p>
        </div>
      </div>
    </div>
  );
}
